import { Box, Button, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import { A11y, Navigation, Pagination } from 'swiper/modules'
import { debounce } from '@/shared/lib'
import { debounceSpeedShort } from '@/shared/constants'
import { Arrow, PlusIcon, VodArrowLeft, VodArrowRight } from '@/shared/ui'
import { Swiper as SwiperType } from 'swiper/types'
import { EpisodeItemInterface } from '@/types/vod/VodItemDetails'
import { useTranslation } from 'react-i18next'
import BasicModal from '@/shared/ui/Modal'
import PinInputField from '@/shared/ui/PinInputField'
import { usePinVerification } from '@/widgets/ProfileScreen/hooks/usePinVerification'
import { getConfigRepository } from '@/shared/getContainer/getConfigRepository'
import { getUseVodItemsDetails } from '@/shared/getContainer/getUseVodItemsDetails'
import { useEpisodesSliderHook } from '../hooks/useEpisodesSliderHook'
import {
    FocusContext,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import {
    DIRECTION_LEFT_FOCUS_KEY,
    DIRECTION_RIGHT_FOCUS_KEY,
} from '@/shared/constants/focusLibraryConstants'
import { SeriesEpisodeSliderItem } from '@/features/VOD/components/SeriesEpisodeSliderItem'

const useItemDetails = await getUseVodItemsDetails()

interface IEpisodeSlider {
    episodes: EpisodeItemInterface[] | undefined
    episodeId: string
    actualSeasonId: string
    getItemDetails?: (
        item: {
            productId: string | null
            assetType: string | undefined
            id: string
        },
        value?: boolean | undefined
    ) => void
}

const ConfigRepository = await getConfigRepository()
const configRepository = ConfigRepository.Instance

export const EpisodeSlider = ({
    episodes,
    episodeId,
    actualSeasonId,
}: IEpisodeSlider) => {
    const theme = useTheme()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { t: translate } = useTranslation()
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)
    const [swipe, setSwipe] = useState<SwiperType>()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const config = configRepository.getLocalConfig()

    // const scrollingRef: MutableRefObject<HTMLDivElement | null> = useRef(null)

    const { getItemDetails } = useItemDetails()

    const {
        pin,
        setPin,
        isPinValid,
        setIsPinValid,
        isPinError,
        setIsPinError,
        shouldCheckPin,
        setShouldCheckPin,
        handleCheckPin,
    } = usePinVerification()

    const {
        selectedEpisode,
        setSelectedEpisode,
        fetchMoreEpisodes,
        handleItemClick,
        setEpisodeList,
        episodeList,
    } = useEpisodesSliderHook({ getItemDetails, episodes, actualSeasonId })

    const handleOpenModal = () => {
        setIsModalOpen((prev) => !prev)
        setShouldCheckPin((prev) => ({ ...prev, shouldCheck: true }))
        isModalOpen &&
            isPinValid &&
            selectedEpisode &&
            getItemDetails?.({
                productId: selectedEpisode.productId,
                assetType: selectedEpisode.productType,
                id: selectedEpisode.productId,
            })
    }

    const handleChangePin = (value: string) => {
        setPin(value)
        setIsPinError('')
    }

    const handleCancelPin = () => {
        setShouldCheckPin({
            shouldCheck: false,
            id: '-1',
        })
        setPin('')
        setIsPinError('')
        setIsPinValid(false)
        setIsModalOpen(false)
    }

    const leftClick = debounce(() => {
        swipe?.slidePrev()
        handleSlideChange()
    }, debounceSpeedShort)

    const rightClick = debounce(() => {
        swipe?.slideNext()
        handleSlideChange()
    }, debounceSpeedShort)

    const handleBuyEpisode = (episode: EpisodeItemInterface) => {
        setIsModalOpen(true)
        setShouldCheckPin((prev) => ({
            ...prev,
            shouldCheck: true,
        }))
        setSelectedEpisode(episode)
    }

    const handleSlideChange = () => {
        if (swipe) {
            setIsAtStart(swipe.isBeginning)
            setIsAtEnd(swipe.isEnd)
            if (swipe.isEnd) {
                fetchMoreEpisodes()
            }
        }
    }

    useEffect(() => {
        if (swipe) {
            swipe.on('slideChange', handleSlideChange)
        }
        return () => {
            swipe?.off('slideChange', handleSlideChange)
        }
    }, [swipe])

    useEffect(() => {
        if (swipe && episodes) {
            setIsAtStart(swipe.isBeginning)
            setIsAtEnd(swipe.isEnd)
            setEpisodeList(episodes)
            const episodeIndex = episodes.findIndex(
                (episode) => episode.productId === episodeId
            )
            if (episodeIndex !== -1) {
                swipe.slideTo(episodeIndex, 0)
            }
        }
    }, [swipe, episodeId])

    useEffect(() => {
        if (!episodes) return
        setEpisodeList(episodes)
    }, [episodes])

    const { ref, focusKey } = useFocusable({
        isFocusBoundary: true,
        focusBoundaryDirections: [
            DIRECTION_LEFT_FOCUS_KEY,
            DIRECTION_RIGHT_FOCUS_KEY,
        ],
    })
    // // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // const onAssetFocus = useCallback(
    //     ({ x }: { x: number }) => {
    //         scrollingRef.current?.scrollTo({
    //             left: x,
    //             behavior: 'smooth',
    //         })
    //     },
    //     [scrollingRef]
    // )

    const modalContainerStyle = {
        maxWidth: '60vw',
        maxHeight: '60vw',
    }

    const buyButtonStyle = {
        transition: 'all 0.2s ease-out',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '8px 12px',
        gap: '8px',
        position: 'absolute',
        width: '112px',
        minWidth: '36px',
        height: '36px',
        minHeight: '36px',
        left: 'calc(50% - 112px/2)',
        top: 'calc(50% - 36px/2)',
        background: theme.palette.primary.main,
        borderRadius: '10px',
        color: 'custom.black',
        fontSize: '13px',
        '&:hover': {
            background: theme.palette.primary.main,
        },
    }

    const arrowWrapperStyle = (
        isAtEdge: boolean,
        direction: 'left' | 'right'
    ) => ({
        position: 'absolute',
        [direction]: 0,
        top: 0,
        width: '50px',
        height: '100%',
        opacity: isAtEdge ? 0 : 1,
        pointerEvents: 'all',
        cursor: 'pointer',
    })

    const progressBarWrapper = {
        position: 'absolute',
        bottom: '20px',
        left: '15%',
        width: '70%',
    }

    const notPurchased = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'custom.black',
        opacity: 0.5,
    }

    const shadowBoxStyle = {
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'black',
        opacity: 0.3,
        top: 0,
        left: 0,
        zIndex: 8,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    const iframeContainerStyle = {
        width: '60vw',
        height: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }

    const arrowContainer = {
        position: 'absolute',
        top: '0px',
        left: '0px',
        width: '100%',
        height: '100%',
    }
    const handleMouseEnter = (item: EpisodeItemInterface) => {
        getItemDetails({
            productId: item.productId,
            assetType: item.productType,
            id: `vod:${item.productId}` || '',
            serviceName: item.serviceName || '',
        })
    }

    if (!episodeList?.length) return <></>

    return (
        <FocusContext.Provider value={focusKey}>
            <Swiper
                key="episode-slider"
                ref={ref}
                onBeforeInit={(swiper: SwiperType) => setSwipe(swiper)}
                modules={[Navigation, Pagination, A11y]}
                spaceBetween={8}
                slidesPerGroup={2}
                slidesPerView="auto"
                style={{ marginTop: '32px' }}>
                {episodeList?.map((episode: EpisodeItemInterface) => (
                    <SwiperSlide
                        key={episode.title}
                        style={{ width: 'min-content' }}
                        onClick={() => {
                            handleItemClick(episode)
                        }}>
                        <SeriesEpisodeSliderItem
                            episode={episode}
                            handleMouseEnter={handleMouseEnter}
                            handleBuyEpisode={handleBuyEpisode}
                            progressBarWrapper={progressBarWrapper}
                            notPurchased={notPurchased}
                            buyButtonStyle={buyButtonStyle}
                        />
                    </SwiperSlide>
                ))}
                <Box sx={arrowContainer}>
                    <Box
                        sx={arrowWrapperStyle(isAtStart, 'left')}
                        onClick={leftClick}>
                        <Arrow icon={<VodArrowLeft />} />
                        <Box sx={shadowBoxStyle} />
                    </Box>
                    <Box
                        sx={arrowWrapperStyle(isAtEnd, 'right')}
                        onClick={rightClick}>
                        <Arrow icon={<VodArrowRight />} />
                        <Box sx={shadowBoxStyle} />
                    </Box>
                </Box>
            </Swiper>
            <BasicModal
                key="buyEpisodeModal"
                isOpen={isModalOpen}
                setIsModalOpen={handleOpenModal}
                styles={modalContainerStyle}>
                <Button
                    onClick={() => {
                        setIsModalOpen(false)
                        isPinValid &&
                            selectedEpisode &&
                            getItemDetails?.({
                                productId: selectedEpisode.productId,
                                assetType: selectedEpisode.productType,
                                id: selectedEpisode.productId,
                            })
                    }}
                    style={{
                        transform: 'rotate(45deg)',
                    }}>
                    <PlusIcon fill={theme.palette.primary.main} />
                </Button>
                <Box width="100%">
                    {shouldCheckPin.shouldCheck && (
                        <PinInputField
                            onChangePin={handleChangePin}
                            onCheckPin={() =>
                                handleCheckPin(pin, shouldCheckPin.id)
                            }
                            onCancelPin={handleCancelPin}
                            isPinError={!!isPinError}
                        />
                    )}
                    {!shouldCheckPin.shouldCheck && isPinValid && (
                        <Box sx={iframeContainerStyle}>
                            <iframe
                                src={config?.rentalUri}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                title="External Content"
                            />
                        </Box>
                    )}
                </Box>
            </BasicModal>
        </FocusContext.Provider>
    )
}
